import { zodResolver as rawResolver } from '@hookform/resolvers/zod';
import type { Resolver, FieldValues } from 'react-hook-form';
import type { z } from 'zod';

/**
 * Type-safe wrapper around `@hookform/resolvers/zod`.
 *
 * Why this exists: the `shadcn` CLI (a devDependency used to scaffold UI
 * components) pulls in `zod-to-json-schema` which transitively keeps
 * `zod@3.25.x` in the workspace. pnpm hoists that copy as the shared
 * `node_modules/.pnpm/node_modules/zod` symlink, so the resolver's
 * `import * as z4 from 'zod/v4/core'` follows the symlink to zod@3.25's
 * compat shim (`version.minor: 0`). Our schemas come from `zod@4.4.3`
 * (`version.minor: 4`), so the literal-typed version field mismatches and
 * `zodResolver(schema)` produces a TS2769. The runtime resolver is fine —
 * it accepts any Zod 4 schema — only the structural .d.ts types diverge.
 *
 * Casting once here keeps every form call site clean and centralises the
 * known peer-skew. If shadcn ever drops its zod-to-json-schema dep, this
 * wrapper can be deleted and `zodResolver` imported directly.
 */
export function zodFormResolver<TSchema extends z.ZodTypeAny>(
  schema: TSchema,
): Resolver<z.input<TSchema> & FieldValues, unknown, z.output<TSchema>> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return rawResolver(schema as any) as Resolver<
    z.input<TSchema> & FieldValues,
    unknown,
    z.output<TSchema>
  >;
}
